<template>
  <div
    class="bg-base-300 fixed top-0 left-0 mr-5 flex h-full max-w-100 min-w-64 flex-col p-2 shadow-lg transition-transform"
    :class="[showMenu ? 'translate-x-0' : '-translate-x-full']"
    ref="element"
  >
    <VersionBadge
      class="badge badge-neutral absolute top-0 left-0 rounded-t-none rounded-l-none md:hidden"
    />
    <button class="btn btn-square btn-ghost ml-auto block" @click="showMenu = false">
      <FontAwesomeIcon :icon="faXmark" class="text-xl" />
    </button>
    <NavigationMenuRoot orientation="vertical" class="grid w-full grow" v-slot="{ modelValue }">
      <NavigationMenuList class="menu menu-lg h-full min-w-full overflow-y-auto p-0">
        <RouterNavItem
          v-for="route in topSection"
          :key="route.path"
          prefix="/"
          :route="route"
          :user-roles="roles"
          :active="route.path === modelValue"
        />
        <li
          class="mt-auto h-0 bg-transparent"
          v-if="topSection.length > 0 && bottomSection.length > 0"
        />
        <div class="mb-4 flex flex-nowrap items-stretch gap-2 max-sm:flex-col md:hidden">
          <div class="flex gap-2">
            <InternalLedgerSessionStatusIndicator
              v-if="
                currentSource?.availableTransactionTypes.includes(
                  OfferTransactionType.INTERNAL_LEDGER_CASH,
                )
              "
            />
            <CurrentSourceInfo
              class="grow"
              trigger-class="btn btn-neutral data-[state='closed']:btn-ghost data-[state='closed']:not-hover:not-focus:not-active:text-base-content px-2 max-sm:w-full"
            />
          </div>
          <div class="divider divider-horizontal m-0 max-sm:hidden" />
          <CurrentUserInfo class="px-2 max-sm:w-full" />
        </div>
        <RouterNavItem
          v-for="route in bottomSection"
          prefix="/"
          :key="route.path"
          :route="route"
          :user-roles="roles"
          :active="route.path === modelValue"
        />
      </NavigationMenuList>
      <div v-if="isDesktop" class="absolute top-0 right-0 h-full translate-x-full pl-2">
        <NavigationMenuViewport
          align="start"
          class="bg-neutral text-neutral-content relative top-(--reka-navigation-menu-viewport-top) mt-10 h-(--reka-navigation-menu-viewport-height) rounded shadow"
        />
      </div>
    </NavigationMenuRoot>
  </div>
</template>

<script setup lang="ts">
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { OfferTransactionType } from '@konsolenkost-de/acquisition_store_server_sdk_vue';
import { onClickOutside } from '@vueuse/core';
import { NavigationMenuList, NavigationMenuRoot, NavigationMenuViewport } from 'reka-ui';
import { computed, useTemplateRef } from 'vue';
import { useRouter } from 'vue-router';
import VersionBadge from '../components/app/VersionBadge.vue';
import InternalLedgerSessionStatusIndicator from '../components/internal-ledger/InternalLedgerSessionStatusIndicator.vue';
import RouterNavItem from '../components/RouterNavItem.vue';
import CurrentSourceInfo from '../components/source/CurrentSourceInfo.vue';
import CurrentUserInfo from '../components/user/CurrentUserInfo.vue';
import { useDeviceType } from '../composables/style/useDeviceType';
import { useNavigationMenu } from '../composables/useNavigationMenu';
import { sortMenuRoutes } from '../router/menu';
import { useAuth } from '../stores/authStore';
import { usePos } from '../stores/posStore';
import { useSource } from '../stores/sourceStore';

const { showMenu } = useNavigationMenu();
const { roles } = useAuth();
const { isPosMode } = usePos();
const { afterEach, options } = useRouter();
const { currentSource } = useSource();

const menuRef = useTemplateRef('element');
const { isDesktop } = useDeviceType();

const routes = computed(() => {
  return options.routes[0]?.children?.filter((route) => route.meta && !route.meta.hide) || [];
});
const isVisible = (route: (typeof routes.value)[number]) =>
  (!route.meta?.requiresRole ||
    route.meta.requiresRole.some((role) => roles.value.includes(role))) &&
  !route.meta?.excludeRoles?.some((role) => roles.value.includes(role)) &&
  !(route.meta?.hideInPos && isPosMode.value) &&
  !(route.meta?.posOnly && !isPosMode.value);

const topSection = computed(() => {
  return sortMenuRoutes(
    routes.value.filter(
      (route) => (!route.meta?.section || route.meta?.section === 'top') && isVisible(route),
    ),
  );
});
const bottomSection = computed(() => {
  return sortMenuRoutes(
    routes.value.filter((route) => route.meta?.section === 'bottom' && isVisible(route)),
  );
});

const openInNewWindow = () => {
  const url = window.location.href;
  window.open(url, '_blank', 'status=0,toolbar=0,menubar=0,location=0');
};

onClickOutside(menuRef, () => void (showMenu.value = false));
afterEach(() => void (showMenu.value = false));
</script>
